import {
  alpha,
  Box,
  Table as MuiTable,
  styled,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Loader } from "components/loader";
// import TableFooter from 'components/Mui/TableFooter'
import _ from "lodash";

export interface ColumnProps {
  dataIndex: string;
  key: string;
  title: any;
  render?: (cellData: any, row: any) => any;
  textColor?: string;
}

interface TableProps {
  columns: Array<ColumnProps>;
  rowKey?: string;
  dataSource?: any[];
  loading?: boolean;
  headAlign?: any;
  tableAlign?: any;
  onRowClick?: (row: any) => void;
  pagination?: {
    page: number;
    count?: number;
    onPageChange: any;
    rowsPerPage: number;
    onRowsPerPageChange: any;
  };
  // When the caller passes the FULL dataset (not a server page slice),
  // set this so the table paginates client-side.
  clientPaginate?: boolean;
}

export default function Table({
  columns,
  dataSource,
  onRowClick,
  pagination,
  loading,
  headAlign,
  tableAlign,
  clientPaginate,
}: TableProps) {
  const rows =
    clientPaginate && pagination
      ? (dataSource || []).slice(
          pagination.page * pagination.rowsPerPage,
          pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
        )
      : dataSource;
  return (
    <TableContainer
      sx={{
        borderRadius: "14px",
        border: "1px solid #ECEEF4",
        borderCollapse: "collapse",
        boxShadow: "0px 6px 22px rgba(30,42,138,0.06)",
        background: "#FFFFFF",
        overflowX: "auto",
      }}
    >
      <>
        <MuiTable sx={{ border: "none", borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <StyledHeadCell align="left" key={index}>
                  {col.title}
                </StyledHeadCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableBody>
              <TableRow>
                <StyledBodyCell colSpan={columns.length} align="center">
                  <Loader />
                </StyledBodyCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              {rows?.length !== 0 ? (
                <TableBody>
                  {rows?.map((row, index) => (
                    <StyledTableRow
                      canRowClick={!!onRowClick}
                      hover={!!onRowClick}
                      isLast={index === (rows?.length || 0) - 1}
                      key={index}
                      onClick={() => {
                        return onRowClick && onRowClick(row);
                      }}
                    >
                      {columns.map((col, colIndex) => (
                        <StyledBodyCell
                          align="left"
                          sx={{ color: col.textColor }}
                          key={colIndex}
                        >
                          {col?.render ? (
                            <Typography
                              component="div"
                              sx={{ fontSize: "14px" }}
                            >
                              {col.render(_.get(row, col.dataIndex), row)}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: "14px" }}>
                              {_.get(row, col.dataIndex)}
                            </Typography>
                          )}
                        </StyledBodyCell>
                      ))}
                    </StyledTableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Typography
                        sx={{
                          padding: "24px 10px",
                          textAlign: "center",
                          color: "#8288A6",
                        }}
                      >
                        No Records Found !..
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </>
          )}
        </MuiTable>
        {pagination && (
          <Box
            display="flex"
            justifyContent="end"
            sx={{ borderTop: "1px solid #ECEEF4" }}
          >
            <TablePagination
              component="div"
              count={pagination.count ?? 0}
              page={pagination.page}
              onPageChange={pagination.onPageChange}
              rowsPerPage={pagination.rowsPerPage}
              onRowsPerPageChange={pagination.onRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </Box>
        )}
      </>
    </TableContainer>
  );
}

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#EEF0FA",
    color: "#1E2A8A",
    fontFamily: "albertSans_semibold",
    fontSize: "12px",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    borderBottom: "none",
    padding: "14px 16px",
    whiteSpace: "nowrap",
  },
}));

const StyledBodyCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    padding: "14px 16px",
    borderBottom: "none",
    color: "#2B2F42",
    verticalAlign: "middle",
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) =>
    prop !== "canRowClick" && prop !== "isLast",
})<{ canRowClick: boolean; isLast?: boolean }>(({ canRowClick, isLast }) => ({
  borderBottom: isLast ? "none" : "1px solid #F1F3F9",
  transition: "background-color 0.15s ease",
  ...(canRowClick && {
    cursor: "pointer",
  }),
  "&:hover": {
    backgroundColor: alpha("#EEF0FA", 0.45),
  },
}));
